import React, {useState} from 'react';
import {
  IonAccordion,
  IonAccordionGroup,
  IonButton,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonNote,
  IonSpinner,
  IonText,
} from '@ionic/react';
import {DateTime} from 'luxon';
import {useAppSelector} from '../../lib/hooks';
import {
  useDetalhesParcial, useHistoricoTransferencias, useItensCompleta, useUserTipo,
} from '../../lib/DataHooks/transferencias';
import {TransferenciaHistorico, TransferenciaItemTransferido} from '../../models/Transferencia';
import {TipoItemParcial} from '../../models/TransferenciaParcial';

const TIPOS_SELECAO: {chave: TipoItemParcial; label: string}[] = [
  {chave: 'fazendas', label: 'fazenda(s)'},
  {chave: 'lojas', label: 'loja(s)'},
  {chave: 'veterinarios', label: 'veterinário(s)'},
];

const TIPOS_ITEM_TRANSFERIDO: {chave: TransferenciaItemTransferido['tipo']; label: string}[] = [
  {chave: 'fazenda', label: 'Fazendas'},
  {chave: 'loja', label: 'Lojas'},
  {chave: 'veterinario', label: 'Veterinários'},
];

const HistoricoTransferenciasPage: React.FC = () => {
  const {historico, isLoading, isLoadingMais, temMais, carregarMais} = useHistoricoTransferencias();
  const {isAdmin} = useUserTipo();

  if (isLoading) {
    return (
      <div className="flex justify-center mt-10">
        <IonSpinner/>
      </div>
    );
  }

  if (historico.length === 0) {
    return (
      <div className="flex justify-center mt-10 px-4 text-center text-gray-500">
        <IonText>
          {isAdmin
            ? 'Nenhuma transferência realizada ainda.'
            : 'Nenhuma transferência encontrada para sua conta.'}
        </IonText>
      </div>
    );
  }

  return (
    <>
      <IonList>
        {historico.map(item => (
          <HistoricoItem key={item.id} item={item}/>
        ))}
      </IonList>

      {temMais && (
        <div className="flex justify-center my-4">
          {isLoadingMais
            ? <IonSpinner/>
            : (
              <IonButton fill="outline" onClick={carregarMais}>
                Carregar mais
              </IonButton>
            )}
        </div>
      )}
    </>
  );
};

const resumoParcial = (item: TransferenciaHistorico): string => {
  const {
    fazendas, lojas, veterinarios,
    fazendas_desativadas, lojas_desativadas, veterinarios_desativadas,
  } = item.resumo;

  const transferidos = [
    fazendas > 0 && `${fazendas} fazenda(s)`,
    lojas > 0 && `${lojas} loja(s)`,
    veterinarios > 0 && `${veterinarios} veterinário(s)`,
  ].filter(Boolean).join(', ');

  const desativados = [
    fazendas_desativadas > 0 && `${fazendas_desativadas} fazenda(s)`,
    lojas_desativadas > 0 && `${lojas_desativadas} loja(s)`,
    veterinarios_desativadas > 0 && `${veterinarios_desativadas} veterinário(s)`,
  ].filter(Boolean).join(', ');

  if (!transferidos && !desativados) return 'nenhum item transferido';

  return [
    transferidos && `${transferidos} transferido(s)`,
    desativados && `${desativados} desativado(s)`,
  ].filter(Boolean).join(', ');
};

const HistoricoItem: React.FC<{item: TransferenciaHistorico}> = ({item}) => {
  const {isAdmin} = useUserTipo();
  const user = useAppSelector(state => state.app.user);
  const [detalhesAbertos, setDetalhesAbertos] = useState(false);
  const {detalhes, isLoading: isLoadingDetalhes, erro: erroDetalhes, carregar: carregarDetalhes} = useDetalhesParcial();
  const {
    itens: itensCompleta, isLoading: isLoadingItensCompleta, erro: erroItensCompleta, carregar: carregarItensCompleta,
  } = useItensCompleta();

  const data = item.criado_em?.toDate
    ? DateTime.fromJSDate(item.criado_em.toDate()).toFormat('dd/MM/yyyy HH:mm')
    : '';

  const statusColor = item.status === 'concluida' ? 'success' : 'danger';
  const abertaPorGestor = item.aberto_por_tipo === 'gestor' || item.criado_por_tipo === 'gestor';

  const podeVerDetalhesParcial = item.tipo === 'parcial'
    && !!item.transferencia_origem_id
    && (isAdmin || user.uid === item.criado_por_uid);

  // Diferente da parcial, os itens da completa não ficam no próprio documento de histórico
  // (que DE/PARA já leem direto via Firestore) — são buscados via endpoint que verifica
  // admin/aberto_por_uid no backend, para não expor a lista exata a outros participantes.
  const podeVerDetalhesCompleta = item.tipo === 'completa' && (isAdmin || user.uid === item.aberto_por_uid);

  const podeVerDetalhes = podeVerDetalhesParcial || podeVerDetalhesCompleta;

  const handleToggleDetalhes = () => {
    if (!detalhesAbertos && !detalhes && podeVerDetalhesParcial && item.transferencia_origem_id) {
      carregarDetalhes(item.transferencia_origem_id);
    }
    if (!detalhesAbertos && !itensCompleta && podeVerDetalhesCompleta) {
      carregarItensCompleta(item.id);
    }
    setDetalhesAbertos(atual => !atual);
  };

  return (
    <IonItem>
      <IonLabel>
        <h2>{item.de_nome} → {item.para_nome}</h2>
        <p>
          <IonText color={statusColor}>{item.status}</IonText>
          {' · '}{item.tipo}{' · '}{data}
        </p>
        {item.realizado_por_nome && (
          <p className="text-xs text-gray-500">Decidido por {item.realizado_por_nome}</p>
        )}
        {item.tipo === 'parcial' && (
          <p className="text-xs text-gray-500">{resumoParcial(item)}</p>
        )}
        {abertaPorGestor && (
          <p className="text-xs text-gray-400">Aberta por gestor regional, aprovada pelo admin</p>
        )}

        {podeVerDetalhes && (
          <div className="mt-2">
            <IonButton fill="clear" size="small" onClick={handleToggleDetalhes}>
              {detalhesAbertos ? 'Ocultar detalhes' : 'Ver detalhes'}
            </IonButton>

            {detalhesAbertos && podeVerDetalhesCompleta && (
              <>
                {isLoadingItensCompleta && (
                  <div className="flex justify-center py-2">
                    <IonSpinner/>
                  </div>
                )}

                {!isLoadingItensCompleta && erroItensCompleta && (
                  <IonNote color="danger" className="block">{erroItensCompleta}</IonNote>
                )}

                {!isLoadingItensCompleta && itensCompleta && (
                  <IonAccordionGroup multiple>
                    {TIPOS_ITEM_TRANSFERIDO.map(({chave, label}) => {
                      const itensDoTipo = itensCompleta.filter(i => i.tipo === chave);
                      return (
                        <IonAccordion key={chave} value={chave}>
                          <IonItem slot="header">
                            <IonLabel>
                              {label}
                              {itensDoTipo.length > 0 ? ` (${itensDoTipo.length})` : ''}
                            </IonLabel>
                          </IonItem>
                          <IonList slot="content">
                            {itensDoTipo.length === 0 && (
                              <IonItem>
                                <IonLabel color="medium">Nenhum item.</IonLabel>
                              </IonItem>
                            )}
                            {itensDoTipo.map(detalheItem => (
                              <IonItem key={detalheItem.id}>
                                <IonLabel>
                                  <div className="flex items-start gap-1.5">
                                    <span
                                      className="mt-1.5 inline-block rounded-full bg-gray-500 shrink-0"
                                      style={{width: 8, height: 8}}
                                    />
                                    <div>
                                      <div className="font-medium">{detalheItem.nome}</div>
                                      {detalheItem.cidade && (
                                        <div className="text-xs text-gray-500">{detalheItem.cidade}</div>
                                      )}
                                    </div>
                                  </div>
                                </IonLabel>
                              </IonItem>
                            ))}
                          </IonList>
                        </IonAccordion>
                      );
                    })}
                  </IonAccordionGroup>
                )}
              </>
            )}

            {detalhesAbertos && podeVerDetalhesParcial && (
              <>
                {isLoadingDetalhes && (
                  <div className="flex justify-center py-2">
                    <IonSpinner/>
                  </div>
                )}

                {!isLoadingDetalhes && erroDetalhes && (
                  <IonNote color="danger" className="block">{erroDetalhes}</IonNote>
                )}

                {!isLoadingDetalhes && detalhes && (
                  <IonAccordionGroup multiple>
                    {TIPOS_SELECAO.map(({chave, label}) => {
                      const totalTipo = detalhes[chave].transferir.length + detalhes[chave].desativar.length;
                      return (
                        <IonAccordion key={chave} value={chave}>
                          <IonItem slot="header">
                            <IonLabel>
                              {label}
                              {totalTipo > 0 ? ` (${totalTipo})` : ''}
                            </IonLabel>
                          </IonItem>
                          <div slot="content">
                            <IonList>
                              <IonListHeader>
                                <IonLabel color="primary">
                                  <span className="text-xs font-semibold uppercase tracking-wide">
                                    Transferir ({detalhes[chave].transferir.length})
                                  </span>
                                </IonLabel>
                              </IonListHeader>
                              {detalhes[chave].transferir.length === 0 && (
                                <IonItem>
                                  <IonLabel color="medium">Nenhum item.</IonLabel>
                                </IonItem>
                              )}
                              {detalhes[chave].transferir.map(detalheItem => (
                                <IonItem key={detalheItem.id}>
                                  <IonLabel>
                                    <div className="flex items-start gap-1.5">
                                      <span
                                        className="mt-1.5 inline-block rounded-full bg-gray-500 shrink-0"
                                        style={{width: 8, height: 8}}
                                      />
                                      <div>
                                        <div className="font-medium">{detalheItem.nome}</div>
                                        {detalheItem.cidade && (
                                          <div className="text-xs text-gray-500">{detalheItem.cidade}</div>
                                        )}
                                      </div>
                                    </div>
                                  </IonLabel>
                                </IonItem>
                              ))}
                            </IonList>

                            <IonList>
                              <IonListHeader>
                                <IonLabel color="danger">
                                  <span className="text-xs font-semibold uppercase tracking-wide">
                                    Desativar ({detalhes[chave].desativar.length})
                                  </span>
                                </IonLabel>
                              </IonListHeader>
                              {detalhes[chave].desativar.length === 0 && (
                                <IonItem>
                                  <IonLabel color="medium">Nenhum item.</IonLabel>
                                </IonItem>
                              )}
                              {detalhes[chave].desativar.map(detalheItem => (
                                <IonItem key={detalheItem.id}>
                                  <IonLabel>
                                    <div className="flex items-start gap-1.5">
                                      <span
                                        className="mt-1.5 inline-block rounded-full bg-gray-500 shrink-0"
                                        style={{width: 8, height: 8}}
                                      />
                                      <div>
                                        <div className="font-medium">{detalheItem.nome}</div>
                                        {detalheItem.cidade && (
                                          <div className="text-xs text-gray-500">{detalheItem.cidade}</div>
                                        )}
                                      </div>
                                    </div>
                                  </IonLabel>
                                </IonItem>
                              ))}
                            </IonList>
                          </div>
                        </IonAccordion>
                      );
                    })}
                  </IonAccordionGroup>
                )}
              </>
            )}
          </div>
        )}
      </IonLabel>
    </IonItem>
  );
};

export default HistoricoTransferenciasPage;
